/**
 * Vision (§2): per-player sensor coverage, live hexes, entity visibility, missile track
 * starts, launch hints and detonation views.
 *
 * Output lives in `state.vision` and `entity.visMask`, designed so the per-player network
 * filter only does bit tests:
 *   isHexLive(state, p, h)  ⇔ (live[h] & teamMask[p]) !== 0
 *   canSee(p, e)            ⇔ (e.visMask >> p) & 1
 *   vision.entered[p] / vision.left[p]  — entity ids whose visibility changed this tick
 *   `track` events          — first tick a missile is inside a team's vision
 * Spectators (eliminated players in FFA, or once their whole team is out) see everything:
 * every hex live, every entity visible, every missile tracked (§1.11).
 */
import { BALANCE } from '../balance.js';
import { allied, bit, isAirborne, isStructure, shipActive, structureActive } from '../entities.js';
import { landHexAt, landInRadius } from '../hex.js';
import { createRng } from '../rng.js';
import type {
  Entity,
  EntityId,
  HexIdx,
  LaunchHint,
  MatchState,
  MissileEntity,
  ObserverDetonation,
  ObserverHint,
  PlayerId,
  SimEvent,
  VisionState,
} from '../types.js';

export function createVisionState(N: number, nPlayers: number): VisionState {
  return {
    teamMask: new Uint16Array(nPlayers + 1),
    terr: new Uint16Array(N),
    cover: new Uint16Array(N),
    live: new Uint16Array(N),
    spectators: 0,
    staticCover: new Uint16Array(N),
    entered: Array.from({ length: nPlayers + 1 }, () => []),
    left: Array.from({ length: nPlayers + 1 }, () => []),
  };
}

/** Hex h is live (current info) for player p. */
export function isHexLive(state: MatchState, p: PlayerId, h: HexIdx): boolean {
  return (state.vision.live[h] & state.vision.teamMask[p]) !== 0;
}

/** Player p's team currently sees entity e. */
export function canSee(p: PlayerId, e: Entity): boolean {
  return ((e.visMask >> p) & 1) === 1;
}

// ---------------------------------------------------------------------------
// Sensor index (per state, rebuilt every tick)
// ---------------------------------------------------------------------------

const CELL = 16;

interface SensorIndex {
  n: number;
  x: Float64Array;
  y: Float64Array;
  ownerBit: Uint16Array;
  /** air / surface range (everything except submerged subs) */
  rAir: Float32Array;
  /** sonar range (subs and ships) */
  rSonar: Float32Array;
  /** short-range radar (submerged subs) */
  rShort: Float32Array;
  cols: number;
  rows: number;
  cells: number[][];
}

const indexes = new WeakMap<MatchState, SensorIndex>();

function sensorIndex(state: MatchState): SensorIndex {
  let s = indexes.get(state);
  if (!s) {
    const cols = Math.ceil(state.world.width / CELL) + 1;
    const rows = Math.ceil(state.world.height / CELL) + 1;
    const cap = 256;
    s = {
      n: 0,
      x: new Float64Array(cap),
      y: new Float64Array(cap),
      ownerBit: new Uint16Array(cap),
      rAir: new Float32Array(cap),
      rSonar: new Float32Array(cap),
      rShort: new Float32Array(cap),
      cols,
      rows,
      cells: Array.from({ length: cols * rows }, () => []),
    };
    indexes.set(state, s);
  }
  return s;
}

function grow(s: SensorIndex): void {
  const cap = s.x.length * 2;
  const g64 = (a: Float64Array) => {
    const b = new Float64Array(cap);
    b.set(a);
    return b;
  };
  const g32 = (a: Float32Array) => {
    const b = new Float32Array(cap);
    b.set(a);
    return b;
  };
  const g16 = new Uint16Array(cap);
  g16.set(s.ownerBit);
  s.x = g64(s.x);
  s.y = g64(s.y);
  s.ownerBit = g16;
  s.rAir = g32(s.rAir);
  s.rSonar = g32(s.rSonar);
  s.rShort = g32(s.rShort);
}

function addSensor(s: SensorIndex, x: number, y: number, owner: PlayerId, rAir: number, rSonar: number, rShort: number): void {
  if (s.n === s.x.length) grow(s);
  const i = s.n++;
  s.x[i] = x;
  s.y[i] = y;
  s.ownerBit[i] = bit(owner);
  s.rAir[i] = rAir;
  s.rSonar[i] = rSonar;
  s.rShort[i] = rShort;
  const r = Math.max(rAir, rSonar, rShort);
  const c0 = Math.max(0, Math.floor((x - r) / CELL));
  const c1 = Math.min(s.cols - 1, Math.floor((x + r) / CELL));
  const r0 = Math.max(0, Math.floor((y - r) / CELL));
  const r1 = Math.min(s.rows - 1, Math.floor((y + r) / CELL));
  for (let row = r0; row <= r1; row++) for (let col = c0; col <= c1; col++) s.cells[row * s.cols + col].push(i);
}

function cellOf(s: SensorIndex, x: number, y: number): number[] | null {
  const col = Math.floor(x / CELL);
  const row = Math.floor(y / CELL);
  if (col < 0 || row < 0 || col >= s.cols || row >= s.rows) return null;
  return s.cells[row * s.cols + col];
}

const enum Detect {
  Air,
  Surface,
  Submerged,
  Exposed,
}

/** Raw (un-expanded) bitset of players whose own sensors detect a target at (x, y). */
function detectBits(s: SensorIndex, x: number, y: number, mode: Detect): number {
  const cell = cellOf(s, x, y);
  if (!cell) return 0;
  let bits = 0;
  for (let k = 0; k < cell.length; k++) {
    const i = cell[k];
    const b = s.ownerBit[i];
    if (bits & b) continue;
    const dx = s.x[i] - x;
    const dy = s.y[i] - y;
    const d2 = dx * dx + dy * dy;
    let r: number;
    switch (mode) {
      case Detect.Air:
        r = s.rAir[i];
        break;
      case Detect.Surface:
      case Detect.Exposed:
        r = Math.max(s.rAir[i], s.rSonar[i]);
        break;
      case Detect.Submerged:
        r = Math.max(s.rSonar[i], s.rShort[i]);
        break;
    }
    if (d2 <= r * r) bits |= b;
  }
  return bits;
}

/** Expand raw per-player bits to the set of players whose team includes one of them. */
function expand(state: MatchState, raw: number): number {
  if (raw === 0) return 0;
  const tm = state.vision.teamMask;
  let out = 0;
  for (let p = 1; p < tm.length; p++) if (raw & tm[p]) out |= 1 << p;
  return out;
}

/** Team-expanded set of players for whom the point (x, y) is inside vision. */
export function pointVisibleMask(state: MatchState, x: number, y: number): number {
  const s = sensorIndex(state);
  let raw = detectBits(s, x, y, Detect.Air);
  const h = landHexAt(state.geo, x, y);
  if (h >= 0) raw |= state.vision.live[h];
  return expand(state, raw) | state.vision.spectators;
}

// ---------------------------------------------------------------------------
// Per-tick computation
// ---------------------------------------------------------------------------

const V = BALANCE.vision;

function structureSensorRange(e: Entity): number {
  switch (e.kind) {
    case 'radar':
      return V.radar;
    case 'sam':
      // a silent SAM radiates nothing: no sensor of its own
      return e.silent ? 0 : V.sam;
    case 'city':
      return V.city;
    default:
      return 0;
  }
}

export function computeVision(state: MatchState, events: SimEvent[]): void {
  const vis = state.vision;
  const w = state.world;
  const N = w.landCount;
  const np = state.players.length;

  // spectators see alone (their omniscient bits never reach a teammate); team masks
  const spect = spectatorBits(state);
  vis.spectators = spect;
  for (let p = 1; p <= np; p++) {
    let m = bit(p);
    if (!(spect & m)) for (let q = 1; q <= np; q++) if (q !== p && !(spect & bit(q)) && allied(state, p, q)) m |= bit(q);
    vis.teamMask[p] = m;
  }

  // static (structure) cover for players whose sensor set changed
  if (state.sensorsDirty) {
    const dirty = state.sensorsDirty;
    state.sensorsDirty = 0;
    const clear = ~dirty & 0xffff;
    const sc = vis.staticCover;
    for (let h = 0; h < N; h++) sc[h] &= clear;
    const tmp: HexIdx[] = [];
    for (const e of state.entities.values()) {
      if (!isStructure(e) || !(dirty & bit(e.owner)) || !structureActive(state, e)) continue;
      const r = structureSensorRange(e);
      if (r <= 0) continue;
      tmp.length = 0;
      landInRadius(state.geo, w, e.x, e.y, r, tmp);
      const b = bit(e.owner);
      for (let k = 0; k < tmp.length; k++) sc[tmp[k]] |= b;
    }
  }

  // sensor index + mobile cover
  const s = sensorIndex(state);
  for (const c of s.cells) c.length = 0;
  s.n = 0;
  vis.cover.set(vis.staticCover);
  const tmp: HexIdx[] = [];
  const mobileDisc = (x: number, y: number, r: number, owner: PlayerId) => {
    tmp.length = 0;
    landInRadius(state.geo, w, x, y, r, tmp);
    const b = bit(owner);
    for (let k = 0; k < tmp.length; k++) vis.cover[tmp[k]] |= b;
  };
  for (const e of state.entities.values()) {
    if (e.owner === 0 || state.players[e.owner - 1].eliminatedTick >= 0) continue;
    switch (e.kind) {
      case 'radar':
        if (structureActive(state, e)) addSensor(s, e.x, e.y, e.owner, V.radar, 0, V.shortRadar);
        break;
      case 'sam':
        if (structureActive(state, e) && !e.silent) addSensor(s, e.x, e.y, e.owner, V.sam, 0, 0);
        break;
      case 'city':
        if (structureActive(state, e)) addSensor(s, e.x, e.y, e.owner, V.city, 0, 0);
        break;
      case 'fighter':
      case 'bomber':
        if (isAirborne(e)) {
          const r = e.kind === 'fighter' ? V.fighter : V.bomber;
          addSensor(s, e.x, e.y, e.owner, r, 0, 0);
          mobileDisc(e.x, e.y, r, e.owner);
        }
        break;
      case 'carrier':
        if (shipActive(state, e)) {
          addSensor(s, e.x, e.y, e.owner, V.carrierRadar, V.carrierSonar, V.shortRadar);
          mobileDisc(e.x, e.y, V.carrierRadar, e.owner);
        }
        break;
      case 'sub':
        if (shipActive(state, e)) addSensor(s, e.x, e.y, e.owner, 0, V.subSonar, 0);
        break;
      default:
        break;
    }
  }

  // territory + one ring
  const terr = vis.terr;
  terr.fill(0);
  const own = state.owner;
  const nbr = w.nbr;
  for (let h = 0; h < N; h++) {
    const o = own[h];
    if (!o) continue;
    const b = 1 << o;
    terr[h] |= b;
    const base = h * 6;
    for (let k = 0; k < 6; k++) {
      const n = nbr[base + k];
      if (n >= 0) terr[n] |= b;
    }
  }
  const live = vis.live;
  const cover = vis.cover;
  if (spect) for (let h = 0; h < N; h++) cover[h] |= spect;
  for (let h = 0; h < N; h++) live[h] = terr[h] | cover[h];

  // entity visibility
  for (let p = 1; p <= np; p++) {
    vis.entered[p].length = 0;
    vis.left[p].length = 0;
  }
  for (const e of state.entities.values()) {
    const prev = e.visMask;
    const next = entityVisMask(state, s, e);
    e.visMask = next;
    const changed = prev ^ next;
    if (changed) {
      for (let p = 1; p <= np; p++) {
        if (!((changed >> p) & 1)) continue;
        if ((next >> p) & 1) vis.entered[p].push(e.id);
        else vis.left[p].push(e.id);
      }
    }
    if (e.kind === 'icbm' || e.kind === 'slbm') {
      const fresh = next & ~e.trackMask;
      if (fresh) {
        e.trackMask |= fresh;
        events.push({ type: 'track', missileId: e.id, players: bitsToPlayers(fresh, np), track: trackOf(e) });
      }
    }
  }
}

function entityVisMask(state: MatchState, s: SensorIndex, e: Entity): number {
  // a SAM that just fired radiated: every player sees it until its exposure runs out
  if (e.kind === 'sam' && state.tick < e.exposedUntil) return ((1 << (state.players.length + 1)) - 2) | state.vision.spectators;
  const ownerTeam = e.owner > 0 ? state.vision.teamMask[e.owner] : 0;
  let mode: Detect | -1;
  switch (e.kind) {
    case 'fighter':
    case 'bomber':
      mode = isAirborne(e) ? Detect.Air : -1;
      break;
    case 'sub':
      mode = !shipActive(state, e) ? Detect.Surface : state.tick < e.exposedUntil ? Detect.Exposed : Detect.Submerged;
      break;
    case 'carrier':
      mode = Detect.Surface;
      break;
    default:
      mode = Detect.Air;
  }
  const raw = mode === -1 ? 0 : detectBits(s, e.x, e.y, mode);
  return expand(state, raw | ownerTeam) | state.vision.spectators;
}

/** Bitset of spectators: eliminated players (FFA), or eliminated players whose whole team is out. */
function spectatorBits(state: MatchState): number {
  let out = 0;
  const teams = state.settings.mode === 'teams';
  for (const p of state.players) {
    if (p.eliminatedTick < 0) continue;
    if (teams && state.players.some((q) => q.team === p.team && q.eliminatedTick < 0)) continue;
    out |= bit(p.id);
  }
  return out;
}

export function bitsToPlayers(bits: number, np: number): PlayerId[] {
  const out: PlayerId[] = [];
  for (let p = 1; p <= np; p++) if ((bits >> p) & 1) out.push(p);
  return out;
}

export function trackOf(m: MissileEntity) {
  return {
    id: m.id,
    kind: m.kind,
    owner: m.owner,
    ox: m.ox,
    oy: m.oy,
    tx: m.tx,
    ty: m.ty,
    launchTick: m.launchTick,
    flightTicks: m.flightTicks,
  };
}

// ---------------------------------------------------------------------------
// Launch hints and detonation views
// ---------------------------------------------------------------------------

/** One jittered hint per non-allied player (seeded per missile × observer). */
export function launchHints(state: MatchState, m: MissileEntity): ObserverHint[] {
  const out: ObserverHint[] = [];
  const trueBearing = Math.atan2(m.ty - m.oy, m.tx - m.ox);
  const bj = (V.hintBearingJitterDeg * Math.PI) / 180;
  const spread = (V.hintSpreadDeg * Math.PI) / 180;
  for (const p of state.players) {
    if (allied(state, p.id, m.owner)) continue;
    const rng = createRng(state.seed, 'hints', m.id, p.id);
    const [jx, jy] = rng.disc(V.hintJitter);
    let bearing = trueBearing + rng.range(-bj, bj);
    if (bearing < 0) bearing += Math.PI * 2;
    if (bearing >= Math.PI * 2) bearing -= Math.PI * 2;
    const hint: LaunchHint = { hintId: state.nextHintId++, x: m.ox + jx, y: m.oy + jy, bearing, spread, tick: state.tick };
    out.push({ observer: p.id, hint });
  }
  return out;
}

/** Exact detonation for players who see the point, ≤3 mu seeded jitter for everyone else. */
export function detonationViews(state: MatchState, x: number, y: number): ObserverDetonation[] {
  const id = state.nextDetonationId++;
  const seen = pointVisibleMask(state, x, y);
  const out: ObserverDetonation[] = [];
  for (const p of state.players) {
    if ((seen >> p.id) & 1) {
      out.push({ observer: p.id, det: { x, y, tick: state.tick, exact: true } });
    } else {
      const [jx, jy] = createRng(state.seed, 'mapjitter', id, p.id).disc(V.detonationJitter);
      out.push({ observer: p.id, det: { x: x + jx, y: y + jy, tick: state.tick, exact: false } });
    }
  }
  return out;
}

/** Ids of entities currently visible to player p (ascending). */
export function visibleEntityIds(state: MatchState, p: PlayerId): EntityId[] {
  const out: EntityId[] = [];
  for (const e of state.entities.values()) if ((e.visMask >> p) & 1) out.push(e.id);
  return out;
}
